/**
 * TAP2BUY Auth Hooks
 *
 * Uses Amplify GraphQL mutations for SMS-based authentication
 */

import { useMutation } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/api';

// Lazy client initialization
let _client: ReturnType<typeof generateClient> | null = null;
function getClient() {
  if (!_client) {
    _client = generateClient();
  }
  return _client;
}

// GraphQL mutations
const mutations = {
  sendMobileCode: /* GraphQL */ `
    mutation SendMobileCode($mobile: String!) {
      sendMobileCode(mobile: $mobile) {
        success
        message
        mobile
      }
    }
  `,
  confirmMobileCode: /* GraphQL */ `
    mutation ConfirmMobileCode($mobile: String!, $code: String!) {
      confirmMobileCode(mobile: $mobile, code: $code) {
        success
        message
        token
        user {
          mobile
          first_name
          last_name
          email
          opt_in_flag
        }
        isNewUser
      }
    }
  `,
  updateCustomerProfile: /* GraphQL */ `
    mutation UpdateCustomerProfile(
      $mobile: String!
      $firstName: String
      $lastName: String
      $email: String
      $address: String
    ) {
      updateCustomerProfile(
        mobile: $mobile
        firstName: $firstName
        lastName: $lastName
        email: $email
        address: $address
      ) {
        success
        message
      }
    }
  `,
};

// Types
export interface AuthUser {
  mobile: string;
  first_name: string;
  last_name: string;
  email: string;
  opt_in_flag: number;
}

export interface SendCodeResult {
  success: boolean;
  message: string;
  mobile?: string;
}

export interface ConfirmCodeResult {
  success: boolean;
  message: string;
  token?: string;
  user?: AuthUser;
  isNewUser?: boolean;
}

export interface UpdateProfileResult {
  success: boolean;
  message: string;
}

export interface UpdateProfileInput {
  mobile: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  address?: string;
}

/**
 * Hook to send SMS verification code
 */
export function useSendMobileCode() {
  return useMutation(
    async (mobile: string): Promise<SendCodeResult> => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: mutations.sendMobileCode,
        variables: { mobile },
      });
      return response.data.sendMobileCode;
    }
  );
}

/**
 * Hook to confirm SMS verification code and login
 */
export function useConfirmMobileCode() {
  return useMutation(
    async ({ mobile, code }: { mobile: string; code: string }): Promise<ConfirmCodeResult> => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: mutations.confirmMobileCode,
        variables: { mobile, code },
      });
      return response.data.confirmMobileCode;
    }
  );
}

/**
 * Hook to update customer profile
 */
export function useUpdateCustomerProfile() {
  return useMutation(
    async (input: UpdateProfileInput): Promise<UpdateProfileResult> => {
      const response: any = await getClient().graphql({
        authMode: 'apiKey',
        query: mutations.updateCustomerProfile,
        variables: input,
      });
      return response.data.updateCustomerProfile;
    }
  );
}

/**
 * Direct function to update customer profile (for use in Redux actions)
 */
export async function updateCustomerProfile(input: UpdateProfileInput): Promise<UpdateProfileResult> {
  const response: any = await getClient().graphql({
    authMode: 'apiKey',
    query: mutations.updateCustomerProfile,
    variables: input,
  });
  return response.data.updateCustomerProfile;
}

/**
 * Store JWT token in localStorage
 */
export function storeAuthToken(token: string): void {
  localStorage.setItem('tap2buy_token', token);
}

/**
 * Get JWT token from localStorage
 */
export function getAuthToken(): string | null {
  return localStorage.getItem('tap2buy_token');
}

/**
 * Clear auth token (logout)
 */
export function clearAuthToken(): void {
  localStorage.removeItem('tap2buy_token');
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  const token = getAuthToken();
  if (!token) return false;

  // Basic JWT expiry check (decode without verification)
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expiry = payload.exp * 1000; // Convert to milliseconds
    return Date.now() < expiry;
  } catch {
    return false;
  }
}

/**
 * Get current user from stored token
 */
export function getCurrentUser(): AuthUser | null {
  const token = getAuthToken();
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      mobile: payload.mobile || payload.sub,
      first_name: payload.first_name || '',
      last_name: payload.last_name || '',
      email: payload.email || '',
      opt_in_flag: 0,
    };
  } catch {
    return null;
  }
}
